/* oxlint-disable no-param-reassign -- https://redux-toolkit.js.org/usage/immer-reducers */
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { match } from 'ts-pattern';
import { z } from 'zod';
import { OrderEditType } from '~/utils/constants';
import {
  orderEditActionSchema,
  orderEditAddDiscountCodeActionSchema,
  orderEditCancelOrderActionSchema,
  orderEditModifyQuantityActionSchema,
  orderEditPreparedSchema,
  orderEditRemovePackageProtection,
  orderEditStagedIdSchema,
  orderEditStagedSchema,
  orderEditUpdateAddressActionSchema,
  orderEditUpdateEmailActionSchema,
  orderEditUpdateShippingLineActionSchema,
} from '~/utils/schemas';
import { orderEditCommit } from './sharedActions';

export type OrderEditAction = z.infer<typeof orderEditActionSchema>;
export type OrderEditStaged = z.infer<typeof orderEditStagedIdSchema>;
export type OrderEditPrepared = z.infer<typeof orderEditPreparedSchema>;

export type OrderEditState = z.infer<typeof orderEditStateSchema>;

const Steps = {
  initial: 'initial',
  editing: 'editing',
  reviewing: 'reviewing',
  completed: 'completed',
} as const;

const initialState = z.object({
  step: z.literal(Steps.initial),
  editType: z.null(),
  action: z.null(),
  stagedEdits: z.null().optional(),
});

const baseEditState = z.object({
  step: z.literal(Steps.editing),
  appliedOrderEditId: z.number().optional(),
});
const editingState = z.discriminatedUnion('editType', [
  baseEditState.extend({
    editType: z.literal(`${OrderEditType.CANCEL_ORDER}`),
    action: orderEditCancelOrderActionSchema,
    stagedEdits: z.never().optional(),
  }),
  baseEditState.extend({
    editType: z.literal(`${OrderEditType.UPDATE_SHIPPING_ADDRESS}`),
    action: orderEditUpdateAddressActionSchema,
    stagedEdits: orderEditStagedSchema.options[1].shape.stagedEdits.nullable(),
  }),
  baseEditState.extend({
    editType: z.literal(`${OrderEditType.REMOVE_PACKAGE_PROTECTION}`),
    action: orderEditRemovePackageProtection,
    stagedEdits: z.never().optional(),
  }),
  baseEditState.extend({
    editType: z.literal(`${OrderEditType.MODIFY_QUANTITY}`),
    action: orderEditModifyQuantityActionSchema,
    stagedEdits: orderEditStagedSchema.options[3].shape.stagedEdits.nullable(),
  }),
  baseEditState.extend({
    editType: z.literal(`${OrderEditType.UPDATE_EMAIL}`),
    action: orderEditUpdateEmailActionSchema,
    stagedEdits: orderEditStagedSchema.options[4].shape.stagedEdits.nullable(),
  }),
  baseEditState.extend({
    editType: z.literal(`${OrderEditType.UPDATE_SHIPPING_LINE}`),
    action: orderEditUpdateShippingLineActionSchema,
    stagedEdits: orderEditStagedSchema.options[5].shape.stagedEdits.nullable(),
    // assignAction sets editType before assignPrepared populates this
    prepared: orderEditPreparedSchema.options[0].shape.prepared.optional(),
  }),
  baseEditState.extend({
    editType: z.literal(`${OrderEditType.ADD_DISCOUNT_CODE}`),
    action: orderEditAddDiscountCodeActionSchema,
    stagedEdits: orderEditStagedSchema.options[6].shape.stagedEdits.nullable(),
  }),
]);

const baseReviewState = z.object({
  step: z.literal(Steps.reviewing),
  appliedOrderEditId: z.number(),
});
const reviewingState = z.discriminatedUnion('editType', [
  baseReviewState.extend({
    editType: z.literal(`${OrderEditType.CANCEL_ORDER}`),
    action: orderEditCancelOrderActionSchema,
    stagedEdits: z.never().optional(),
  }),
  baseReviewState.extend({
    editType: z.literal(`${OrderEditType.UPDATE_SHIPPING_ADDRESS}`),
    action: orderEditUpdateAddressActionSchema,
    stagedEdits: orderEditStagedSchema.options[1].shape.stagedEdits,
  }),
  baseReviewState.extend({
    editType: z.literal(`${OrderEditType.REMOVE_PACKAGE_PROTECTION}`),
    action: orderEditRemovePackageProtection,
    stagedEdits: z.never().optional(),
  }),
  baseReviewState.extend({
    editType: z.literal(`${OrderEditType.MODIFY_QUANTITY}`),
    action: orderEditModifyQuantityActionSchema,
    stagedEdits: orderEditStagedSchema.options[3].shape.stagedEdits,
  }),
  baseReviewState.extend({
    editType: z.literal(`${OrderEditType.UPDATE_EMAIL}`),
    action: orderEditUpdateEmailActionSchema,
    stagedEdits: orderEditStagedSchema.options[4].shape.stagedEdits,
  }),
  baseReviewState.extend({
    editType: z.literal(`${OrderEditType.UPDATE_SHIPPING_LINE}`),
    action: orderEditUpdateShippingLineActionSchema,
    stagedEdits: orderEditStagedSchema.options[5].shape.stagedEdits,
    prepared: orderEditPreparedSchema.options[0].shape.prepared,
  }),
  baseReviewState.extend({
    editType: z.literal(`${OrderEditType.ADD_DISCOUNT_CODE}`),
    action: orderEditAddDiscountCodeActionSchema,
    stagedEdits: orderEditStagedSchema.options[6].shape.stagedEdits,
  }),
]);

const completedState = initialState.extend({
  step: z.literal(Steps.completed),
});

export const orderEditStateSchema = z.union([
  initialState,
  editingState,
  reviewingState,
  completedState,
]);

export const orderEditSlice = createSlice({
  name: 'orderEdit',
  // oxlint-disable-next-line typescript/no-unnecessary-type-assertion
  initialState: {
    step: Steps.initial,
    editType: null,
    action: null,
  } as z.infer<typeof orderEditStateSchema>,
  reducers: {
    assignAction: (state, action: PayloadAction<OrderEditAction>) => {
      state.step = Steps.editing;
      state.editType = action.payload.editType;
      state.action = action.payload;
      // clear any previous staged edits
      state.stagedEdits = undefined;
    },

    assignPrepared: (state, action: PayloadAction<OrderEditPrepared>) => {
      if (state.editType !== action.payload.editType) {
        throw new Error('State mismatch');
      }

      match(action.payload)
        .with({ editType: 'UPDATE_SHIPPING_LINE' }, (p) => {
          state.prepared = p.prepared;
        })
        .exhaustive();
    },

    assignEdits: (state, action: PayloadAction<OrderEditStaged>) => {
      if (
        !(state.step === Steps.editing || state.step === Steps.reviewing) ||
        state.editType !== action.payload.editType
      ) {
        throw new Error('State mismatch');
      }

      if (
        action.payload.editType !== 'CANCEL_ORDER' &&
        action.payload.editType !== 'REMOVE_PACKAGE_PROTECTION'
      ) {
        state.stagedEdits = action.payload.stagedEdits;
      }

      state.step = Steps.reviewing;
      state.appliedOrderEditId = action.payload.appliedOrderEditId;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(orderEditCommit, () => ({
      step: Steps.completed,
      editType: null,
      action: null,
      // clear any previous staged edits
      stagedEdits: undefined,
    }));
  },
});
